import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { getWatchBoard, getWatchHandoff } from '../../truckmateStore';
import { geocode } from '../../traccar';
import TripCard from './TripCard';
import DetailPanel from './DetailPanel';
import { crewModel, stopsModel, handlingModel, incidentsForTrip, estimateArrival, heldAtHub } from './model';
import './dispatch.css';

// The Active trips workspace: one card per truck/trip + the selected-trip
// panel. Selection and every open section are held by the PAGE (props), so the
// 60-second live refresh and tab switches never reset them.

const GEO_KEY = 'tm-zip-geo';
const zipOf = (s) => { const m = String(s || '').match(/\b(\d{5})\b/); return m ? m[1] : ''; };
export const readGeo = () => { try { return JSON.parse(localStorage.getItem(GEO_KEY) || '{}'); } catch { return {}; } };
const haversineMi = (a, b) => {
  const R = 3958.8; const r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r; const dLng = (b.lng - a.lng) * r;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

function handoffText(h) {
  const t = (ms) => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const lines = [`SHIFT BRIEF — ${new Date(h.generatedAt).toLocaleString()}`, `Prepared by ${h.by} · ${h.tripCount} active trips watched`, '', `OPEN (${h.open.length})`];
  if (!h.open.length) lines.push('  Nothing open.');
  h.open.forEach((a) => lines.push(`  ${a.severity === 'critical' ? '[CRITICAL]' : '[WARNING]'} ${a.unit ? `Truck ${a.unit} · ` : ''}${a.trip ? `Trip ${a.trip} · ` : ''}${a.title}`, `      Next: ${a.detail}`, `      ${a.ack ? `Owner: ${a.ack.by} since ${t(a.ack.at)}` : 'NO OWNER'}`));
  lines.push('', `RESOLVED LAST 12H (${h.resolved.length})`);
  h.resolved.forEach((a) => lines.push(`  ${a.unit ? `Truck ${a.unit} · ` : ''}${a.title} — ${a.resolvedBy === 'auto' ? 'cleared on its own' : `closed by ${a.resolvedBy}`}${a.note ? ` (“${a.note}”)` : ''}`));
  return lines.join('\n');
}

// "TAYLOR, MI, 48180" → "Taylor, MI" (the ZIP dropped, the city in normal case). Pure.
export const cityState = (x) => {
  const parts = String(x || '').replace(/,?\s*\d{5}(-\d{4})?\s*$/, '').split(',').map((v) => v.trim()).filter(Boolean);
  if (!parts.length) return '';
  const city = parts[0].toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
  return parts[1] ? `${city}, ${parts[1].toUpperCase()}` : city;
};

// A load dispatch booked in TruckMate but hasn't put a truck on yet (assigned / dispatched / planned, no truck) —
// kept in a folded group at the bottom so the trips on the road stay on top. Pure.
export const bookedNoTruck = (t) => !String(t.powerUnit || '').trim() && /^(ASSGN|ASSIGNED|DISP|AVAIL|AVBL|PLAN|NEW|SPTLD|PRINTED)/i.test(String(t.statusCode || t.status || ''));
// how long this truck has been stopped (the watch board's clock), or null while it's moving. Pure.
export function stoppedFor(trip, board) {
  const u = String(trip.powerUnit || '').trim().toLowerCase().replace(/^0+(?=\d)/, '');
  const s = u && board && board.stopped ? board.stopped[u] : null;
  return s && s.since ? { since: s.since, unknown: !!s.unknown } : null;
}
// per-trip "at a glance" values (crew, next stop, miles to it, ETA, appointment…) from the trip, the watch board
// and the cached ZIP coordinates — shared by the Active trips cards and the Fleet map truck card. Pure.
export function tripGlance(trip, board, geo = {}) {
  const open = (board && board.open) || [];
  const crew = crewModel(trip);
  const stops = stopsModel(trip);
  const zipOfStop = (st) => (st ? (zipOf(st.place) || zipOf((trip.bills.find((b) => st.bills.includes(b.billNumber)) || {}).to)) : '');
  // the server knows which stops the truck already drove past (TruckMate may not show them delivered yet) →
  // the next stop is the first one still ahead
  const srv0 = board && board.etas && board.etas[trip.tripNumber];
  const srvFresh = !!(srv0 && Date.now() - srv0.at < 5 * 60000);
  const passedZips = new Set(srvFresh ? (srv0.passed || []).map((p) => p.zip).filter(Boolean) : []);
  let target = stops.current || stops.next;
  if (!stops.current && target && passedZips.has(zipOfStop(target))) {
    // only forward from the passed stop — the server's next stop first, else the first one not passed
    const ahead = stops.stops.slice(stops.stops.indexOf(target) + 1).filter((st) => st.status !== 'completed');
    const srvNext = srv0.stops && srv0.stops[0] && srv0.stops[0].zip;
    target = ahead.find((st) => srvNext && zipOfStop(st) === srvNext) || ahead.find((st) => !passedZips.has(zipOfStop(st))) || target;
  }
  // not picked up yet → the truck is heading to the shipper
  const toShipper = !stops.current && /^(disp|assgn|assigned|avail|avbl|plan|printed|new)/i.test(String(trip.statusCode || '')) && !trip.queuedBehind;
  const zip = toShipper ? zipOf(trip.origin) : zipOfStop(target);
  const l = trip.live || {};
  const c = zip ? geo[zip] : null;
  const remainingMi = c && l.lat != null && !stops.current ? Math.round(haversineMi({ lat: l.lat, lng: l.lng }, c) * 1.2) : null;
  const wheel = crew.drivers.find((d) => crew.atWheel.verified && d.name === crew.atWheel.name) || crew.drivers[0];
  const hos = wheel && !wheel.stale ? (wheel.role === 'Driver 1' ? l.hos : l.hos2) : null;
  // Prefer the server's ETA (same math as the late alerts: both team clocks,
  // 70-hr cycle, road miles); fall back to a local estimate when it's stale.
  const srv = srv0;
  const fresh = srvFresh && !stops.current && !toShipper;
  const leg = fresh ? (srv.stops.find((x) => zip && x.zip === zip) || srv.stops[0]) : null;
  // a next load waiting behind the truck's current run: its clock starts when that run is done
  const qb = trip.queuedBehind || null;
  // still at our Miami / Ventura hub, not departed → no ETA while it sits (says so instead)
  const hubHold = qb ? null : heldAtHub(trip);
  const eta = hubHold ? null : qb ? (qb.pickupEtaMs || null) : leg ? leg.etaMs : remainingMi ? estimateArrival(remainingMi, { team: crew.mode === 'Team', driveLeftMin: hos ? hos.driveLeftMin : null, shiftLeftMin: hos ? hos.shiftLeftMin : null }) : null;
  return {
    crew,
    nextZip: zip,
    remainingMi: leg ? leg.miles : remainingMi,
    // miles to wherever the truck is heading: the next stop, the shipper, or (a next load) its pickup
    milesTo: qb ? (qb.milesToPickup != null ? qb.milesToPickup : null) : stops.current ? null : leg ? leg.miles : remainingMi,
    milesToLabel: qb ? 'to the pickup' : toShipper ? 'to the shipper' : 'to go',
    eta,
    etaSource: hubHold ? 'hub' : qb ? (qb.pickupEtaMs ? 'after current trip' : null) : leg ? 'server' : eta ? 'local' : null,
    hubHold,
    queuedBehind: qb,
    appointment: (() => {
      // nearest appointment from the current/next stop onward, with which stop it is
      const from = target ? stops.stops.indexOf(target) : 0;
      const a = stops.stops.slice(Math.max(0, from)).find((x) => x.appointment && x.status !== 'completed');
      return a ? `${a.number != null ? `Stop ${a.number} · ` : ''}${a.appointment}${a.appointmentSource === 'handwritten' ? ' (handwritten)' : ''}` : null;
    })(),
    atStop: !!stops.current,
    // the stop the truck is heading to (stops it already drove past skipped) — for the outbound list and the panel
    nextStopName: !stops.current && target && !toShipper && !qb ? [target.customer || target.place, cityState(target.place || (trip.bills.find((b) => (target.bills || []).includes(b.billNumber)) || {}).to)].filter(Boolean).join(' — ') : null,
    nextLabel: !qb && toShipper ? `Shipper · ${cityState(trip.origin) || 'pickup'}` : qb ? `Pickup · ${cityState(trip.origin)} — after trip ${qb.trip}${qb.emptyAt ? ` (empty at ${cityState(qb.emptyAt)})` : ''}` : target ? `${target.number != null ? `Stop ${target.number} · ` : ''}${[target.customer, cityState(target.place || (trip.bills.find((b) => (target.bills || []).includes(b.billNumber)) || {}).to)].filter(Boolean).join(' — ')}` : null,
    handling: handlingModel(trip),
    stopped: stoppedFor(trip, board),
    incidents: incidentsForTrip(trip, open),
    // every stop's ETA + miles (server route, refreshed every few minutes) — shown on each stop
    stopEtas: srv0 && Date.now() - srv0.at < 30 * 60000 ? srv0 : null,
  };
}

export default function DispatchWorkspace({ trips, allTrips, metaOf, renderMap, selectedId, onSelect, isOpen, setOpen, briefReq, onChanged, onError, onCounts }) {
  const [board, setBoard] = useState(null);
  const [geo, setGeo] = useState(readGeo);
  const [brief, setBrief] = useState(null);

  const loadBoard = useCallback(() => getWatchBoard().then((b) => { setBoard(b); if (onCounts && b && b.counts) onCounts(b.counts); }).catch(() => setBoard((b) => b)), [onCounts]);
  useEffect(() => { loadBoard(); const id = setInterval(loadBoard, 30000); return () => clearInterval(id); }, [loadBoard]);

  // shift brief (from the page header button)
  useEffect(() => {
    if (!briefReq) return;
    setBrief({ text: '' });
    getWatchHandoff().then((h) => setBrief({ text: handoffText(h) })).catch((e) => setBrief({ error: e.message || 'Could not build the shift brief.' }));
  }, [briefReq]);

  // per-trip "at a glance" values
  const glanceFor = useCallback((trip) => tripGlance(trip, board, geo), [board, geo]);
  const glances = useMemo(() => {
    const out = {};
    trips.forEach((trip) => { out[trip.tripNumber] = glanceFor(trip); });
    return out;
  }, [trips, glanceFor]);

  // geocode next-stop ZIPs we haven't cached yet (a few at a time, cached forever)
  useEffect(() => {
    const need = [...new Set(Object.values(glances).map((g) => g.nextZip).filter((z) => z && !(z in geo)))].slice(0, 8);
    if (!need.length) return undefined;
    let cancelled = false;
    (async () => {
      const add = {};
      for (const z of need) {
        if (cancelled) return;
        try { const r = await geocode({ zip: z }); add[z] = r && r.lat != null ? { lat: r.lat, lng: r.lng } : null; } catch { add[z] = null; } // eslint-disable-line no-await-in-loop
      }
      if (cancelled) return;
      setGeo((g) => {
        const next = { ...g, ...add };
        try { localStorage.setItem(GEO_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [glances, geo]);

  // the panel shows the EXPLICITLY selected trip — looked up in all trips, so
  // a search/filter or scrolling never swaps it for a neighbouring card
  const pool = allTrips || trips;
  const selected = pool.find((t) => String(t.tripNumber) === String(selectedId)) || null;
  const selectedShown = !!selected && trips.some((t) => String(t.tripNumber) === String(selectedId));
  const [cardVisible, setCardVisible] = useState(true);
  useEffect(() => {
    if (!selectedShown) { setCardVisible(false); return undefined; }
    const el = document.getElementById(`tmw-trip-${selectedId}`);
    if (!el || typeof IntersectionObserver === 'undefined') { setCardVisible(true); return undefined; }
    const io = new IntersectionObserver(([e]) => setCardVisible(e.isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => io.disconnect();
  }, [selectedId, selectedShown, trips.length]);
  const jump = () => {
    const el = document.getElementById(`tmw-trip-${selectedId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else onError(`Truck ${selected && selected.powerUnit ? selected.powerUnit : ''} is hidden by the current search or filters.`);
  };
  const changed = () => { loadBoard(); if (onChanged) onChanged(); };
  const booked = trips.filter(bookedNoTruck);
  // open by itself when it's all there is (a search for one of them) or the selected trip is in it
  const [bookedPick, setBookedOpen] = useState(null);
  const bookedOpen = bookedPick != null ? bookedPick : (booked.length > 0 && (booked.length === trips.length || booked.some((t) => String(t.tripNumber) === String(selectedId))));
  const card = (t) => (
    <div id={`tmw-trip-${t.tripNumber}`} key={t.id || t.tripNumber}>
      <TripCard trip={t} meta={metaOf(t)} glance={glances[t.tripNumber]} incidents={glances[t.tripNumber].incidents}
        selected={String(t.tripNumber) === String(selectedId)} onSelect={() => { setBrief(null); onSelect(t.tripNumber); }}
        isOpen={isOpen} setOpen={setOpen} renderMap={renderMap} onError={onError} onChanged={changed} />
    </div>
  );

  return (
    <div className="tmw-layout">
      <section className="tmw-list" aria-label="Active trips">
        <div className="tmw-count"><span>{trips.length} trip{trips.length === 1 ? '' : 's'} shown</span><span>Related alerts grouped per trip</span></div>
        {!trips.length && <div className="tmw-empty">No trips match the current search or filters.</div>}
        {trips.filter((t) => !bookedNoTruck(t)).map(card)}
        {booked.length > 0 && (
          <details className="tmw-booked" open={bookedOpen} onToggle={(e) => setBookedOpen(e.currentTarget.open)}>
            <summary><b>Booked — no truck yet</b> <small>{booked.length}</small> <span className="tmw-small">loads dispatch booked in TruckMate without a truck or driver on them yet</span></summary>
            {booked.map(card)}
          </details>
        )}
      </section>
      <DetailPanel trip={selected} glance={selected ? (glances[selected.tripNumber] || glanceFor(selected)) : null} incidents={selected ? (glances[selected.tripNumber] || glanceFor(selected)).incidents : null}
        resolved={(board && board.resolved) || []} brief={brief} onCloseBrief={() => setBrief(null)} onChanged={changed} onJump={jump}
        onClose={() => onSelect('')} cardVisible={cardVisible} />
    </div>
  );
}
